import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Star, 
  Calendar, 
  Search, 
  Share2, 
  Trash2, 
  X, 
  Brain,
  Target,
  Users,
  Zap,
  DollarSign,
  Code,
  Sparkles,
  Lightbulb,
  Copy,
  LogOut,
  ArrowRight
} from 'lucide-react';
import { AppConcept } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { updateConcept, deleteConcept as deleteConceptFromFirebase, loadUserConcepts } from '../services/firebaseService';

interface IdeaGalleryProps {
  concepts: AppConcept[];
  onBack: () => void;
  onGenerateNew: () => void;
  onConceptsUpdate?: (updatedConcepts: AppConcept[]) => void;
}

const IdeaGallery: React.FC<IdeaGalleryProps> = ({ concepts, onBack, onGenerateNew, onConceptsUpdate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState<'all' | 'starred' | 'recent'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'rating'>('newest');
  const [selectedConcept, setSelectedConcept] = useState<AppConcept | null>(null);
  const [conceptToDelete, setConceptToDelete] = useState<AppConcept | null>(null);
  const [localConcepts, setLocalConcepts] = useState<AppConcept[]>(concepts);

  // Update local concepts when props change
  React.useEffect(() => {
    console.log('🔄 IdeaGallery: Props concepts changed, count:', concepts.length);
    const prevCount = localConcepts.length;
    setLocalConcepts(concepts);
    
    // Check if we should show a specific concept from navigation state
    const showConceptId = (location.state as any)?.showConceptId;
    if (showConceptId && concepts.length > 0) {
      const conceptToShow = concepts.find(c => c.id === showConceptId);
      if (conceptToShow) {
        console.log('🎯 Showing specific concept from navigation:', conceptToShow.appName);
        setSelectedConcept(conceptToShow);
        // Clear the navigation state
        navigate('/gallery', { replace: true, state: {} });
        return;
      }
    }
    
    // If we got a new concept (count increased), auto-show the latest one
    if (concepts.length > prevCount && concepts.length > 0 && !showConceptId) {
      console.log('🎯 New concept detected, auto-showing latest concept');
      const sortedConcepts = [...concepts].sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      const latestConcept = sortedConcepts[0];
      setTimeout(() => {
        setSelectedConcept(latestConcept);
      }, 500);
    }
  }, [concepts, localConcepts.length, location.state, navigate]);

  // Refresh concepts from Firebase when component mounts or user changes
  React.useEffect(() => {
    const refreshConcepts = async () => {
      if (currentUser) {
        try {
          console.log('🔄 IdeaGallery: Refreshing concepts from Firebase for user:', currentUser.uid);
          const freshConcepts = await loadUserConcepts(currentUser.uid);
          console.log('📋 IdeaGallery: Loaded fresh concepts count:', freshConcepts.length);
          setLocalConcepts(freshConcepts);
          onConceptsUpdate?.(freshConcepts);
          
          // Update localStorage as backup when Firebase succeeds
          if (freshConcepts.length > 0) {
            localStorage.setItem('showerThoughtsConcepts', JSON.stringify(freshConcepts));
            console.log('💾 Updated localStorage backup from gallery');
          }
        } catch (error) {
          console.error('❌ Error refreshing concepts in gallery:', error);
          console.log('🔄 Firebase failed in gallery, keeping existing concepts from props');
          
          // Don't override if we already have concepts from props (loaded from localStorage fallback)
          if (concepts.length > 0) {
            console.log('📋 Using concepts from props instead:', concepts.length);
            setLocalConcepts(concepts);
          } else {
            // If no props concepts, try localStorage as last resort
            const saved = localStorage.getItem('showerThoughtsConcepts');
            if (saved) {
              try {
                const localConcepts = JSON.parse(saved);
                console.log('📋 Gallery: Loaded from localStorage as fallback:', localConcepts.length);
                setLocalConcepts(localConcepts);
                onConceptsUpdate?.(localConcepts);
              } catch (parseError) {
                console.error('❌ Gallery: Error parsing localStorage data:', parseError);
              }
            }
          }
        }
      }
    };
    
    // Only refresh if we don't already have concepts from props
    if (concepts.length === 0) {
      refreshConcepts();
    }
  }, [currentUser, onConceptsUpdate, concepts]);

  const filteredConcepts = localConcepts
    .filter(concept => {
      if (filterBy === 'starred') return concept.rating && concept.rating >= 4;
      if (filterBy === 'recent') return new Date(concept.createdAt).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000;
      return true;
    })
    .filter(concept => 
      concept.appName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      concept.tagline.toLowerCase().includes(searchTerm.toLowerCase()) ||
      concept.originalIdea.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortBy === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      return 0;
    });

  // Debug current state
  React.useEffect(() => {
    console.log('📊 IdeaGallery state:', {
      localConceptsCount: localConcepts.length,
      propsConceptsCount: concepts.length,
      currentUser: !!currentUser,
      filteredCount: filteredConcepts.length
    });
  }, [localConcepts, concepts, currentUser, filteredConcepts.length]);

  const handleRate = async (conceptId: string, rating: number) => {
    const updated = localConcepts.map(concept => 
      concept.id === conceptId ? { ...concept, rating } : concept
    );
    setLocalConcepts(updated);
    onConceptsUpdate?.(updated);
    
    // Update in Firebase
    if (currentUser) {
      try {
        const conceptToUpdate = updated.find(c => c.id === conceptId);
        if (conceptToUpdate) {
          await updateConcept(conceptToUpdate, currentUser.uid);
        }
      } catch (error) {
        console.error('Error updating concept rating:', error);
      }
    }
  };

  const handleDelete = (concept: AppConcept) => {
    console.log('🗑️ Delete button clicked for concept:', concept.appName);
    console.log('📋 Current concepts count before delete:', localConcepts.length);
    setConceptToDelete(concept);
  };

  const confirmDelete = async () => {
    if (conceptToDelete) {
      console.log('🗑️ Deleting concept:', conceptToDelete.appName);
      
      // Update UI immediately for instant feedback
      const updated = localConcepts.filter(concept => concept.id !== conceptToDelete.id);
      console.log('📋 Updated local concepts count:', updated.length);
      
      setLocalConcepts(updated);
      onConceptsUpdate?.(updated);
      
      // Update localStorage immediately
      try {
        localStorage.setItem('showerThoughtsConcepts', JSON.stringify(updated));
        console.log('💾 Updated localStorage after deletion');
      } catch (localError) {
        console.error('❌ Error updating localStorage:', localError);
      }
      
      setConceptToDelete(null);
      
      // If we're viewing the deleted concept, go back to gallery
      if (selectedConcept && selectedConcept.id === conceptToDelete.id) {
        setSelectedConcept(null);
      }
      
      // Handle Firebase deletion in background (non-blocking)
      if (currentUser) {
        deleteConceptFromFirebase(conceptToDelete.id, currentUser.uid)
          .then(() => {
            console.log('✅ Deleted from Firebase:', conceptToDelete.id);
          })
          .catch((error) => {
            console.error('❌ Error deleting from Firebase (but UI already updated):', error);
          });
      }
      
      console.log('✅ Concept deleted instantly (UI updated)');
    }
  };

  const cancelDelete = () => {
    setConceptToDelete(null);
  };

  const handleShare = async (concept: AppConcept) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${concept.appName} - App Concept`,
          text: `Check out this app concept: ${concept.appName} - ${concept.tagline}`,
          url: window.location.href,
        });
      } catch (err) {
        navigator.clipboard.writeText(`${concept.appName} - ${concept.tagline}\n\n${concept.problem}`);
        alert('Concept copied to clipboard!');
      }
    } else {
      navigator.clipboard.writeText(`${concept.appName} - ${concept.tagline}\n\n${concept.problem}`);
      alert('Concept copied to clipboard!');
    }
  };

  const generateMVPForm = (concept: AppConcept) => {
    const targetAudienceText = Array.isArray(concept.targetAudience) 
      ? concept.targetAudience.join(', ') 
      : concept.targetAudience;

    const featuresText = concept.coreFeatures.map((feature, index) => 
      `Feature ${index + 1}: ${feature.title} - ${feature.description}`
    ).join('\n\n');

    const techStackText = concept.techStack.join(', ');

    return `MVP Idea Submission Form

1. App/Product Name:
${concept.appName}

2. One-Line Pitch:
${concept.tagline}

3. Problem Statement:
${concept.problem}

4. Target Users:
${targetAudienceText}

5. Core Features (MVP only):
${featuresText}

6. User Flow (Simple steps):
[User opens app] → [User describes their need] → [App provides solution] → [User achieves goal]

7. Monetization Plan (if any):
${concept.monetization}

8. Tech Stack (Optional):
${techStackText}`;
  };

  const handleCopyMVPForm = (concept: AppConcept) => {
    const mvpFormText = generateMVPForm(concept);
    navigator.clipboard.writeText(mvpFormText);
    alert('MVP form copied to clipboard!');
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (selectedConcept) {
    return (
      <div className="professional-concept-view">
        {/* Header */}
        <div className="concept-header">
          <div className="header-nav">
            <button onClick={() => setSelectedConcept(null)} className="back-btn">
              <ArrowLeft size={20} />
              <span>Back to Gallery</span>
            </button>
          </div>
          
          <div className="concept-hero">
            <div className="hero-main-card">
              <div className="hero-app-icon">
                <span className="hero-app-emoji">{selectedConcept.emoji || '💡'}</span>
              </div>
              <div className="hero-content">
                <h1 className="hero-app-title">{selectedConcept.appName}</h1>
                <p className="hero-app-tagline">"{selectedConcept.tagline}"</p>
                <div className="hero-meta-row">
                  <div className="hero-meta-item">
                    <Calendar size={16} />
                    <span>Created {formatDate(selectedConcept.createdAt)}</span>
                  </div>
                  <div className="hero-meta-item">
                    <Users size={16} />
                    <span>{Array.isArray(selectedConcept.targetAudience) ? selectedConcept.targetAudience.length : 1} Target Groups</span>
                  </div>
                  <div className="hero-meta-item">
                    <Zap size={16} />
                    <span>{selectedConcept.coreFeatures.length} Core Features</span>
                  </div>
                </div>
                <div className="hero-rating">
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star
                      key={star}
                      size={16}
                      className={`hero-rating-star ${selectedConcept.rating && star <= selectedConcept.rating ? 'filled' : ''}`}
                      onClick={() => handleRate(selectedConcept.id, star)}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            <div className="hero-actions">
              <button onClick={() => handleCopyMVPForm(selectedConcept)} className="hero-action-btn primary">
                <Copy size={16} />
                <span>Copy MVP Form</span>
              </button>
              <button onClick={() => handleShare(selectedConcept)} className="hero-action-btn secondary">
                <Share2 size={16} />
                <span>Share</span>
              </button>
              <button onClick={() => handleDelete(selectedConcept)} className="hero-action-btn danger">
                <Trash2 size={16} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="concept-content">
          
          {/* MVP Submission Form Section */}
          <div className="mvp-form-section">
            <div className="section-header">
              <h2 className="section-title">
                <Copy size={24} />
                MVP Submission Form
              </h2>
              <p className="section-subtitle">Ready-to-use pitch format for investors, accelerators, and partners</p>
            </div>
            
            <div className="mvp-form-grid">
              <div className="mvp-card product-name">
                <div className="mvp-card-header">
                  <div className="mvp-card-icon">
                    <Sparkles size={20} />
                  </div>
                  <h3>1. App/Product Name</h3>
                </div>
                <div className="mvp-card-content">
                  <div className="mvp-value">{selectedConcept.appName}</div>
                </div>
              </div>
              
              <div className="mvp-card pitch">
                <div className="mvp-card-header">
                  <div className="mvp-card-icon">
                    <Lightbulb size={20} />
                  </div>
                  <h3>2. One-Line Pitch</h3>
                </div>
                <div className="mvp-card-content">
                  <div className="mvp-value">{selectedConcept.tagline}</div>
                </div>
              </div>
              
              <div className="mvp-card problem-statement">
                <div className="mvp-card-header">
                  <div className="mvp-card-icon">
                    <Target size={20} />
                  </div>
                  <h3>3. Problem Statement</h3>
                </div>
                <div className="mvp-card-content">
                  <div className="mvp-value">{selectedConcept.problem}</div>
                </div>
              </div>
              
              <div className="mvp-card target-users">
                <div className="mvp-card-header">
                  <div className="mvp-card-icon">
                    <Users size={20} />
                  </div>
                  <h3>4. Target Users</h3>
                </div>
                <div className="mvp-card-content">
                  <div className="mvp-value">
                    {Array.isArray(selectedConcept.targetAudience) 
                      ? selectedConcept.targetAudience.join(', ') 
                      : selectedConcept.targetAudience}
                  </div>
                </div>
              </div>
              
              <div className="mvp-card core-features">
                <div className="mvp-card-header">
                  <div className="mvp-card-icon">
                    <Zap size={20} />
                  </div>
                  <h3>5. Core Features (MVP only)</h3>
                </div>
                <div className="mvp-card-content">
                  <div className="mvp-features-list">
                    {selectedConcept.coreFeatures.map((feature, index) => (
                      <div key={index} className="mvp-feature-item">
                        <div className="feature-number">{index + 1}</div>
                        <div className="feature-details">
                          <strong>{feature.title}</strong>
                          <p>{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mvp-card user-flow">
                <div className="mvp-card-header">
                  <div className="mvp-card-icon">
                    <ArrowRight size={20} />
                  </div>
                  <h3>6. User Flow (Simple steps)</h3>
                </div>
                <div className="mvp-card-content">
                  <div className="mvp-value flow-steps">
                    <span className="flow-step">User opens app</span>
                    <span className="flow-arrow">→</span>
                    <span className="flow-step">User describes their need</span>
                    <span className="flow-arrow">→</span>
                    <span className="flow-step">App provides solution</span>
                    <span className="flow-arrow">→</span>
                    <span className="flow-step">User achieves goal</span>
                  </div>
                </div>
              </div>
              
              <div className="mvp-card monetization">
                <div className="mvp-card-header">
                  <div className="mvp-card-icon">
                    <DollarSign size={20} />
                  </div>
                  <h3>7. Monetization Plan</h3>
                </div>
                <div className="mvp-card-content">
                  <div className="mvp-value">{selectedConcept.monetization}</div>
                </div>
              </div>
              
              <div className="mvp-card tech-stack-card">
                <div className="mvp-card-header">
                  <div className="mvp-card-icon">
                    <Code size={20} />
                  </div>
                  <h3>8. Tech Stack</h3>
                </div>
                <div className="mvp-card-content">
                  <div className="mvp-tech-tags">
                    {selectedConcept.techStack.map((tech, index) => (
                      <span key={index} className="mvp-tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div className="detailed-breakdown">
            <div className="section-header">
              <h2 className="section-title">
                <Lightbulb size={24} />
                Detailed Concept Breakdown
              </h2>
              <p className="section-subtitle">Deep dive into your concept's foundation and structure</p>
            </div>
            
            <div className="breakdown-grid">
              {/* Original Inspiration */}
              <div className="breakdown-card inspiration">
                <div className="card-header">
                  <Sparkles size={20} />
                  <h3>Original Shower Thought</h3>
                </div>
                <div className="card-content">
                  <blockquote>"{selectedConcept.originalIdea}"</blockquote>
                  <p>This was the spark that started everything - your raw, unfiltered idea that captured a real need.</p>
                </div>
              </div>
              
              {/* Problem Deep Dive */}
              <div className="breakdown-card problem">
                <div className="card-header">
                  <Target size={20} />
                  <h3>Problem Analysis</h3>
                </div>
                <div className="card-content">
                  <p>{selectedConcept.problem}</p>
                  <div className="problem-impact">
                    <strong>Why This Matters:</strong> This problem affects your target audience daily, creating friction and inefficiency in their lives.
                  </div>
                </div>
              </div>
              
              {/* Market & Users */}
              <div className="breakdown-card audience">
                <div className="card-header">
                  <Users size={20} />
                  <h3>Target Market</h3>
                </div>
                <div className="card-content">
                  <div className="audience-segments">
                    {(Array.isArray(selectedConcept.targetAudience) ? selectedConcept.targetAudience : [selectedConcept.targetAudience]).map((audience, index) => (
                      <div key={index} className="audience-segment">
                        <div className="segment-icon">👥</div>
                        <div className="segment-info">
                          <strong>{audience}</strong>
                          <p>Primary user segment with high engagement potential</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* MVP Strategy */}
              <div className="breakdown-card mvp">
                <div className="card-header">
                  <Star size={20} />
                  <h3>MVP Strategy</h3>
                </div>
                <div className="card-content">
                  <p>{selectedConcept.mvpScope}</p>
                  <div className="mvp-benefits">
                    <strong>Launch Strategy:</strong> Start lean, validate quickly, iterate based on user feedback.
                  </div>
                </div>
              </div>
              
              {/* Revenue Model */}
              <div className="breakdown-card revenue">
                <div className="card-header">
                  <DollarSign size={20} />
                  <h3>Revenue Model</h3>
                </div>
                <div className="card-content">
                  <p>{selectedConcept.monetization}</p>
                  <div className="revenue-timeline">
                    <strong>Timeline:</strong> Focus on user acquisition first, then optimize for revenue growth.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Architecture */}
          <div className="tech-architecture">
            <div className="section-header">
              <h2 className="section-title">
                <Code size={24} />
                Technical Architecture
              </h2>
              <p className="section-subtitle">Recommended technology stack and implementation approach</p>
            </div>
            
            <div className="tech-breakdown">
              <div className="tech-categories">
                <div className="tech-category">
                  <h4>Frontend & UI</h4>
                  <div className="tech-items">
                    {selectedConcept.techStack.filter(tech => 
                      tech.toLowerCase().includes('react') || 
                      tech.toLowerCase().includes('vue') || 
                      tech.toLowerCase().includes('angular') ||
                      tech.toLowerCase().includes('html') ||
                      tech.toLowerCase().includes('css') ||
                      tech.toLowerCase().includes('javascript') ||
                      tech.toLowerCase().includes('typescript') ||
                      tech.toLowerCase().includes('tailwind') ||
                      tech.toLowerCase().includes('bootstrap')
                    ).map((tech, index) => (
                      <span key={index} className="tech-item frontend">{tech}</span>
                    ))}
                  </div>
                </div>
                
                <div className="tech-category">
                  <h4>Backend & Database</h4>
                  <div className="tech-items">
                    {selectedConcept.techStack.filter(tech => 
                      tech.toLowerCase().includes('node') || 
                      tech.toLowerCase().includes('python') || 
                      tech.toLowerCase().includes('postgres') ||
                      tech.toLowerCase().includes('mongo') ||
                      tech.toLowerCase().includes('firebase') ||
                      tech.toLowerCase().includes('express') ||
                      tech.toLowerCase().includes('django') ||
                      tech.toLowerCase().includes('sql')
                    ).map((tech, index) => (
                      <span key={index} className="tech-item backend">{tech}</span>
                    ))}
                  </div>
                </div>
                
                <div className="tech-category">
                  <h4>Cloud & Infrastructure</h4>
                  <div className="tech-items">
                    {selectedConcept.techStack.filter(tech => 
                      tech.toLowerCase().includes('aws') || 
                      tech.toLowerCase().includes('vercel') || 
                      tech.toLowerCase().includes('netlify') ||
                      tech.toLowerCase().includes('heroku') ||
                      tech.toLowerCase().includes('docker') ||
                      tech.toLowerCase().includes('kubernetes')
                    ).map((tech, index) => (
                      <span key={index} className="tech-item cloud">{tech}</span>
                    ))}
                  </div>
                </div>
                
                <div className="tech-category">
                  <h4>Additional Technologies</h4>
                  <div className="tech-items">
                    {selectedConcept.techStack.filter(tech => 
                      !tech.toLowerCase().includes('react') && 
                      !tech.toLowerCase().includes('vue') && 
                      !tech.toLowerCase().includes('angular') &&
                      !tech.toLowerCase().includes('node') && 
                      !tech.toLowerCase().includes('python') && 
                      !tech.toLowerCase().includes('postgres') &&
                      !tech.toLowerCase().includes('mongo') &&
                      !tech.toLowerCase().includes('firebase') &&
                      !tech.toLowerCase().includes('aws') && 
                      !tech.toLowerCase().includes('vercel') && 
                      !tech.toLowerCase().includes('netlify') &&
                      !tech.toLowerCase().includes('html') &&
                      !tech.toLowerCase().includes('css') &&
                      !tech.toLowerCase().includes('javascript') &&
                      !tech.toLowerCase().includes('typescript') &&
                      !tech.toLowerCase().includes('express') &&
                      !tech.toLowerCase().includes('django') &&
                      !tech.toLowerCase().includes('sql') &&
                      !tech.toLowerCase().includes('tailwind') &&
                      !tech.toLowerCase().includes('bootstrap') &&
                      !tech.toLowerCase().includes('heroku') &&
                      !tech.toLowerCase().includes('docker') &&
                      !tech.toLowerCase().includes('kubernetes')
                    ).map((tech, index) => (
                      <span key={index} className="tech-item other">{tech}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal for Detail View */}
        {conceptToDelete && (
          <div className="modal-overlay" onClick={cancelDelete}>
            <div className="modern-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-icon danger">
                  <Trash2 size={24} />
                </div>
                <h3 className="modal-title">Delete Concept</h3>
                <button onClick={cancelDelete} className="modal-close">
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="concept-to-delete">
                  <div className="concept-preview">
                    <div className="concept-icon-preview">
                      <span className="concept-emoji-preview">{conceptToDelete.emoji || '💡'}</span>
                    </div>
                    <div className="concept-info-preview">
                      <h4 className="concept-name-preview">{conceptToDelete.name || conceptToDelete.appName}</h4>
                      <p className="concept-tagline-preview">"{conceptToDelete.tagline}"</p>
                    </div>
                  </div>
                </div>
                <p className="modal-description">
                  Are you sure you want to delete this concept? This action cannot be undone and all data will be permanently lost.
                </p>
              </div>
              <div className="modal-footer">
                <button onClick={cancelDelete} className="modal-btn secondary">
                  Cancel
                </button>
                <button onClick={confirmDelete} className="modal-btn danger">
                  <Trash2 size={16} />
                  Delete Concept
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="modern-gallery">
      {/* Navigation Header */}
      <nav className="gallery-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <Brain size={24} />
            <span>Concept Gallery</span>
          </div>
          <div className="nav-actions">
            <button onClick={() => navigate('/generate')} className="nav-btn primary">
              <Plus size={16} />
              Generate New
            </button>
            <button onClick={logout} className="nav-btn secondary">
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <div className="gallery-header">
        <div className="gallery-container">
          <div className="gallery-title-section">
            <h1 className="gallery-title">Your Concepts</h1>
            <p className="gallery-subtitle">
              {localConcepts.length === 0 ? 'No concepts yet' : 
               localConcepts.length === 1 ? '1 shower thought transformed' :
               `${localConcepts.length} shower thoughts transformed into app concepts`}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="gallery-container">
        {/* Search and Filters */}
        <div className="gallery-controls">
          <div className="search-section">
            <div className="search-input-wrapper">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search your concepts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          
          <div className="filters-section">
            <select value={filterBy} onChange={(e) => setFilterBy(e.target.value as any)} className="filter-select">
              <option value="all">All Concepts</option>
              <option value="starred">Starred Only</option>
              <option value="recent">Recent (7 days)</option>
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="filter-select">
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="rating">Highest Rated</option>
            </select>
          </div>
        </div>

        {/* Empty State or Concepts Grid */}
        {filteredConcepts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Lightbulb size={48} />
            </div>
            <h2 className="empty-title">
              {searchTerm ? "No concepts found" : "No concepts yet"}
            </h2>
            <p className="empty-subtitle">
              {searchTerm 
                ? "Try adjusting your search or filters"
                : "Transform your shower thoughts into app concepts with AI"
              }
            </p>
            <button onClick={() => navigate('/generate')} className="empty-action">
              <Plus size={16} />
              Generate Your First Concept
            </button>
          </div>
        ) : (
          <div className="concepts-grid">
            {filteredConcepts.map((concept) => (
              <div 
                key={concept.id} 
                className="concept-card"
                onClick={() => setSelectedConcept(concept)}
              >
                <div className="card-header">
                  <div className="concept-emoji">{concept.emoji || '💡'}</div>
                  <div className="rating-stars">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star
                        key={star}
                        size={14}
                        className={`rating-star ${concept.rating && star <= concept.rating ? 'filled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRate(concept.id, star);
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div className="card-content">
                  <h3 className="concept-title">{concept.appName}</h3>
                  <p className="concept-tagline">"{concept.tagline}"</p>
                  <p className="concept-idea">From: "{concept.originalIdea}"</p>
                </div>

                <div className="card-footer">
                  <div className="concept-meta">
                    <span className="meta-item">
                      <Zap size={14} />
                      {concept.coreFeatures.length} features
                    </span>
                    <span className="meta-item">
                      <Calendar size={14} />
                      {formatDate(concept.createdAt)}
                    </span>
                  </div>
                  
                  <div className="card-actions">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyMVPForm(concept);
                      }}
                      className="action-btn copy"
                      title="Copy MVP Form"
                    >
                      <Copy size={14} />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShare(concept);
                      }}
                      className="action-btn share"
                      title="Share"
                    >
                      <Share2 size={14} />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(concept);
                      }}
                      className="action-btn delete"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {conceptToDelete && (
        <div className="modal-overlay" onClick={cancelDelete} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="modern-modal" onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: 'white',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div className="modal-header" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem'
            }}>
              <div className="modal-icon danger" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Trash2 size={24} style={{ color: '#dc2626' }} />
                <h3 className="modal-title" style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: 0
                }}>Delete Concept</h3>
              </div>
              <button onClick={cancelDelete} className="modal-close" style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem'
              }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
              <div className="concept-to-delete">
                <div className="concept-preview" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: '#f9fafb',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <div className="concept-icon-preview">
                    <span className="concept-emoji-preview" style={{ fontSize: '2rem' }}>
                      {conceptToDelete.emoji || '💡'}
                    </span>
                  </div>
                  <div className="concept-info-preview">
                    <h4 className="concept-name-preview" style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      margin: '0 0 0.25rem 0'
                    }}>{conceptToDelete.name || conceptToDelete.appName}</h4>
                    <p className="concept-tagline-preview" style={{
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      margin: 0,
                      fontStyle: 'italic'
                    }}>"{conceptToDelete.tagline}"</p>
                  </div>
                </div>
              </div>
              <p className="modal-description" style={{
                color: '#6b7280',
                lineHeight: '1.5',
                margin: 0
              }}>
                Are you sure you want to delete this concept? This action cannot be undone and all data will be permanently lost.
              </p>
            </div>
            <div className="modal-footer" style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              <button onClick={cancelDelete} style={{
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                Cancel
              </button>
              <button onClick={confirmDelete} style={{
                padding: '0.5rem 1rem',
                border: '1px solid #dc2626',
                borderRadius: '0.5rem',
                background: '#dc2626',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Trash2 size={16} />
                Delete Concept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeaGallery;